import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { fromEvent, Subscription, throttleTime } from 'rxjs';

/**
 * Welcome to the base overlay pop-up component, hope it will ease your work with dropdowns!
 *
 * # PLEASE DO NOT CHANGE
 * To make changes here, ask Ivan or Ivan. Every improvement is heartily welcomed, but let's discuss it first.
 *
 * ### How to use:
 *
 * ```html
 * <app-overlay-pop-up [openOnHover]="!openOnClick"> <!-- some other inputs you might need to configure -->
 *   <!-- trigger content goes b̶r̶r̶r̶a̶ here -->
 *   <div overlay-trigger>
 *     <div class="your-very-important-trigger-content-stuff"></div>
 *   </div>
 *
 *   <!-- Overlay Content goes here, please follow the class hierarchy 👑 -->
 *   <!-- obviously, add yours if needed, but do not change ones that exist and follow the hierarchy -->
 *   <div overlay-content>
 *     <div class="overlay-dropdown">
 *       <div class="overlay-list">
 *         <div class="list-block">
 *           <!-- important to give a class 'not-in-table' in case your pop-up is not in a table -->
 *           <div *ngFor="amazingItem of amazingDev" class="list-entry (?)not-in-table"></div>
 *         </div>
 *       </div>
 *     </div>
 *   </div>
 * </app-overlay-pop-up>
 * ```
 * ### Why `ViewEncapsulation.None`?
 *
 * The `encapsulation: ViewEncapsulation.None` setting is used because the `cdkConnectedOverlay`
 * directive projects the overlay content outside the component's view hierarchy. This means that
 * with the default encapsulation mode (`ViewEncapsulation.Emulated`), styles defined in this component
 * would not apply to the overlay content, as it is rendered in a different part of the DOM.
 */
@Component({
  selector: 'app-overlay-pop-up',
  templateUrl: './overlay-pop-up.component.html',
  styleUrls: ['./overlay-pop-up.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
  ],
})
export class OverlayPopUpComponent implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private ngZone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  /**
   * By default, the overlay will open and close on click.
   * If true, the overlay will open on hover and close on mouse leave.
   */
  @Input() openOnHover = false;

  /** If true, the overlay will close when a user clicks inside it. */
  @Input() closeOnInsideClick = true;

  /** If true, the overlay will close when a user clicks outside of it. */
  @Input() closeOnOutsideClick = true;

  /** If true, the overlay width will match the width of the trigger element. */
  @Input() matchTriggerWidth = true;

  /** If true, the overlay's open functionality is disabled. */
  @Input() isDisabled = false;

  /**
   * If true, the overlay will stay in a fixed position below the trigger element,
   * Default is `false`, allowing the overlay to reposition (e.g. be above the trigger, if the space below is not enough to show the dropdown)
   */
  @Input() blockRepositioning = false;

  /** CSS class applied to the overlay content for styling purposes. Hopefully you ain't going to need another one*/
  @Input() overlayContentClass: string | string[] = 'overlay-pop-up';

  /** Reference to the overlay content element, used for click/hover event handling. */
  @ViewChild('overlayContent') overlayContent!: ElementRef;

  /** Determines whether the overlay is currently open or closed. */
  isOverlayContentOpen = false;

  position: ConnectedPosition[];
  private mouseMoveSubscription?: Subscription;

  ngOnInit(): void {
    this.position = this.blockRepositioning ? belowTriggerPosition : null;
  }

  ngOnDestroy(): void {
    this.detachMouseMoveListener();
  }

  toggleOverlay(): void {
    if (this.isDisabled) {
      return;
    }
    this.isOverlayContentOpen = !this.isOverlayContentOpen;
  }

  onMouseEnter(): void {
    if (this.isDisabled || !this.openOnHover || this.isOverlayContentOpen) {
      return;
    }
    this.isOverlayContentOpen = true;
    this.attachMouseMoveListener();
  }

  /**
   * Handles clicks outside the overlay to close it, if {@link closeOnOutsideClick} was set to true.
   * The overlay closes only when a click occurs outside both the trigger and the overlay content.
   */
  onOutsideClick(event: MouseEvent): void {
    if (!this.closeOnOutsideClick) {
      return;
    }
    // allows to close pop-up when clicking on its trigger second time
    const clickedOutside = !(this.elementRef.nativeElement as HTMLElement).contains(event.target as Node);
    if (clickedOutside) {
      this.isOverlayContentOpen = false;
    }
  }

  /**
   * If {@link closeOnInsideClick} is true, the overlay will close when clicked inside.
   */
  onInsideClick(): void {
    if (this.closeOnInsideClick) {
      this.isOverlayContentOpen = false;
    }
  }

  private attachMouseMoveListener(): void {
    this.detachMouseMoveListener();
    this.ngZone.runOutsideAngular(() => {
      this.mouseMoveSubscription = fromEvent<MouseEvent>(document, 'mousemove')
        .pipe(throttleTime(200)) // handling each mousemove will be way too much, so fire event each 200ms
        .subscribe((mouseEvent: MouseEvent): void => {
          this.handleMouseMove(mouseEvent);
        });
    });
  }

  private detachMouseMoveListener(): void {
    if (this.mouseMoveSubscription) {
      this.mouseMoveSubscription.unsubscribe();
      this.mouseMoveSubscription = undefined;
    }
  }

  /**
   * Used only if {@link openOnHover} is true.
   *
   * Monitors mouse movement to determine if the cursor has left the combined
   * bounding box of both the trigger element and the overlay content. If the
   * cursor moves outside that merged region, the overlay will be closed and
   * the mouse move listener is unsubscribed.
   *
   * - **Bounding rectangles** of the trigger and overlay content are retrieved
   *   separately, then merged into a single `combinedRect`.
   * - If the `clientX` and `clientY` of the event fall outside `combinedRect`,
   *   the overlay is closed.
   * - Automatically unsubscribes from mouse move events when closing the overlay to avoid unnecessary event processing.
   */
  private handleMouseMove(mouseEvent: MouseEvent): void {
    if (!this.isOverlayContentOpen || this.isDisabled) {
      this.detachMouseMoveListener();
      return;
    }
    const { clientX, clientY } = mouseEvent;
    const triggerRect = (this.elementRef.nativeElement as HTMLElement)?.getBoundingClientRect();
    const overlayRect = (this.overlayContent.nativeElement as HTMLElement)?.getBoundingClientRect();

    if (!triggerRect && !overlayRect) {
      this.closeOverlayAndDetachListener();
      return;
    }

    const combinedRect = this.getCombinedRect(triggerRect, overlayRect);

    const isOutside =
      clientX < combinedRect.left || clientX > combinedRect.right ||
      clientY < combinedRect.top || clientY > combinedRect.bottom;

    if (isOutside) {
      this.closeOverlayAndDetachListener();
    }
  }

  /**
   * Calculates the combined bounding rectangle of the trigger element and the overlay content.
   * If either rectangle is missing, adjusts the combined rectangle accordingly.
   * Adds a 1px tolerance margin to all sides of the combined rectangle to prevent
   * unwanted closing when the mouse is at the exact edge of the combined area.
   * This tolerance helps avoid edge cases in mouse position detection.
   */
  private getCombinedRect(triggerRect: DOMRect, overlayRect: DOMRect): RectPosition {
    if (!triggerRect) {
      return overlayRect;
    }
    if (!overlayRect) {
      return triggerRect;
    }
    const tolerancePx = 1;
    return {
      left: Math.min(triggerRect.left, overlayRect.left) - tolerancePx,
      right: Math.max(triggerRect.right, overlayRect.right) + tolerancePx,
      top: Math.min(triggerRect.top, overlayRect.top) - tolerancePx,
      bottom: Math.max(triggerRect.bottom, overlayRect.bottom) + tolerancePx,
    };
  }

  private closeOverlayAndDetachListener(): void {
    this.ngZone.run(() => {
      this.isOverlayContentOpen = false;
      this.detachMouseMoveListener();
      this.cdr.detectChanges();
    });
  }
}

const belowTriggerPosition: ConnectedPosition[] = [
  {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
  },
];

interface RectPosition {
  left: number;
  right: number;
  top: number;
  bottom: number;
}
